import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { farmAPI, recordsAPI } from '../../services/api';
import { useFarm } from '../../contexts/FarmContext';
import FlockTimingFields from '../../components/farm/FlockTimingFields';
import { dateInputToApi, parseAgeDays, todayInputValue, biologicalAgeDays } from '../../utils/flockAge';

const FarmManagement = () => {
  const { farmId } = useParams();
  const { currentFarm, getFarm, loading } = useFarm();
  const [newFlock, setNewFlock] = useState({
    name: '',
    type: 'broiler',
    subtype: '',
    breed: '',
    startQty: '',
    startDate: todayInputValue(),
    chickAge: '0',
    targetMarketAgeDays: '',
    housingType: '',
    feedingSystem: '',
    managementSystem: ''
  });
  const breedOptions = {
    layer: ['Hy-Line Brown','Hy-Line White','Lohmann Brown','Lohmann White','ISA Brown','Dekalb White','Rhode Island Red','White Leghorn','Black Star','Red Star'],
    broiler: ['Cobb 500','Ross 308','Ross 708','Arbor Acres','Hubbard','Marshall','Indian River','Cornish Cross','Red Ranger','Freedom Ranger'],
    dualImproved: ['Kenbro','Kuroiler','Sasso','Rainbow Rooster'],
    dualPure: ['Indigenous (Kienyeji)']
  };
  const housingOptions = ['free_range','barn','cage','pasture','deep_litter','other'];
  const feedingOptions = ['manual','automatic','trough','chain_feeder','other'];

  useEffect(() => {
    if (farmId) {
      getFarm(farmId);
    }
  }, [farmId]);

  const [cohorts, setCohorts] = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [cohortBirds, setCohortBirds] = useState(null);
  const [ambientTempC, setAmbientTempC] = useState('24');
  useEffect(() => {
    (async () => {
      try {
        const res = await farmAPI.listCohorts(farmId);
        setCohorts(res.data?.cohorts || []);
      } catch (_) {}
    })();
  }, [farmId]);

  // Load net birds for selected cohort
  useEffect(() => {
    (async () => {
      try {
        if (!selectedCohortId) { setCohortBirds(null); return; }
        const b = await recordsAPI.getBirds(farmId, { cohortId: selectedCohortId });
        setCohortBirds(b.data || null);
      } catch (_) { setCohortBirds(null); }
    })();
  }, [farmId, selectedCohortId]);

  // Helpers: compute age and feed/water targets
  const computeAgeDays = (c) => {
    if (!c) return 0;
    const sd = c.startDate || c.start_date;
    const base = Number(c.initialAgeDays || c.chickAge || 0);
    if (!sd) return base;
    return biologicalAgeDays(sd, base);
  };

  const getFeedPhaseAndTarget = (type, subtype, ageDays) => {
    // Returns { phase, feedGPerBird }
    if (type === 'broiler') {
      const week = Math.max(1, Math.ceil((ageDays || 0) / 7));
      let phase = week <= 3 ? 'Starter' : (week <= 6 ? 'Grower' : 'Finisher');
      const map = { 1: 30, 2: 55, 3: 80, 4: 105, 5: 125, 6: 145, 7: 150, 8: 155 };
      const feed = map[Math.min(8, week)] || 120;
      return { phase, feedGPerBird: feed };
    }
    if (type === 'layer') {
      const w = Math.max(1, Math.ceil((ageDays || 0) / 7));
      let phase = w <= 6 ? 'Chick' : (w <= 16 ? 'Grower' : (w <= 18 ? 'Pre‑lay' : 'Layer'));
      let feed = 30;
      if (w <= 2) feed = 30; else if (w <= 6) feed = 50; else if (w <= 16) feed = 70; else if (w <= 18) feed = 85; else feed = 110;
      return { phase, feedGPerBird: feed };
    }
    // dual: kienyeji
    const w = Math.max(1, Math.ceil((ageDays || 0) / 7));
    let base = 0;
    if (subtype === 'kienyeji_improved') {
      // Slightly below broiler growth targets
      const map = { 1: 28, 2: 50, 3: 72, 4: 95, 5: 112, 6: 130 };
      base = map[Math.min(6, w)] || 110;
      const phase = w <= 4 ? 'Starter' : (w <= 16 ? 'Grower' : (w <= 18 ? 'Pre‑lay' : 'Layer'));
      if (w > 18) base = 105;
      return { phase, feedGPerBird: base };
    }
    // Pure kienyeji: lower still
    const map = { 1: 25, 2: 45, 3: 65, 4: 85, 5: 100, 6: 115 };
    base = map[Math.min(6, w)] || 100;
    const phase = w <= 4 ? 'Starter' : (w <= 20 ? 'Grower' : (w <= 24 ? 'Pre‑lay' : 'Layer'));
    if (w > 24) base = 95;
    return { phase, feedGPerBird: base };
  };

  const getWaterTargetMl = (type, feedGPerBird, ambientC) => {
    const ratio = type === 'broiler' ? 1.9 : 1.8;
    const base = feedGPerBird * ratio;
    const t = Number(ambientC || 24);
    const delta = Math.max(0, t - 21);
    const steps = Math.floor(delta / 5);
    const adjusted = base * (1 + steps * 0.05);
    return Math.round(adjusted);
  };

  const getIngredientTips = (type, subtype, phase) => {
    const tips = [];
    const isKienyeji = type === 'dual';
    const isImproved = isKienyeji && subtype === 'kienyeji_improved';
    const isPure = isKienyeji && subtype === 'kienyeji_pure';
    const norm = (s) => (s || '').toLowerCase();
    const p = norm(phase);
    if (type === 'broiler' || isImproved) {
      if (p.includes('starter') || p.includes('chick')) {
        tips.push('22–24% protein (soy/fish meal): supports rapid muscle and organ development.');
        tips.push('High energy (maize/corn + oil): fuels growth and maintains brooding warmth.');
        tips.push('Amino acids (lysine, methionine): improves weight gain and uniformity.');
        tips.push('Minerals (Ca/P via limestone + dicalcium phosphate): proper bone growth.');
        tips.push('Coccidiostat (starter phase): reduces coccidiosis risk during brooding.');
        tips.push('Vitamin premix + probiotics: strengthens immunity and gut health.');
      } else if (p.includes('grower')) {
        tips.push('18–20% protein: sustains steady growth with lower protein wastage.');
        tips.push('Balanced energy (maize/corn): optimizes feed conversion ratio.');
        tips.push('Amino acids: maintains frame growth and breast yield.');
        tips.push('Electrolytes in heat: supports water intake and reduces stress.');
      } else if (p.includes('finisher')) {
        tips.push('16–18% protein with higher energy: maximizes finish weight efficiently.');
        tips.push('Avoid coccidiostats near market: respect withdrawal times for safety.');
        tips.push('Oil/fat inclusion: boosts energy density without excess bulk.');
      }
    }
    if (type === 'layer' || (isKienyeji && (p.includes('pre‑lay') || p.includes('layer')))) {
      if (p.includes('pre‑lay')) {
        tips.push('Elevated calcium (2.0–2.5%): prepares medullary bone before first egg.');
        tips.push('Phosphorus 0.45–0.5%: balances Ca for bone mineralization.');
        tips.push('Vitamin D3: improves calcium absorption and shell quality.');
      } else if (p.includes('layer')) {
        tips.push('Calcium 3.5–4.0% (limestone + oyster shell): strong shells and fewer cracks.');
        tips.push('Protein 16–18% with methionine: supports egg mass and persistency.');
        tips.push('Omega‑3 sources (e.g., linseed) optional: enriches egg profile.');
        tips.push('Consistent particle size: steadier intake and better shell quality.');
      }
      if (p.includes('chick') || p.includes('grower')) {
        tips.push('Balanced vitamins/minerals: skeletal frame and early immunity for future lay.');
      }
    }
    if (isPure && (p.includes('starter') || p.includes('grower'))) {
      tips.push('Moderate protein (starter 20–21%, grower 16–17%): matches slower growth.');
      tips.push('Fiber sources (sunflower/bran): resilience on semi‑intensive/free‑range.');
      tips.push('Mineral premix: prevents deficiency under scavenging conditions.');
    }
    // Fallback generic tips
    if (tips.length === 0) {
      tips.push('Use a reputable complete ration for the current phase to avoid imbalances.');
      tips.push('Ensure clean, fresh water at all times to match feed targets.');
    }
    return tips;
  };

  const logFeed = async (c, birds, phase, feedPer) => {
    if (!currentFarm || !c?.id) return;
    const qtyKg = Math.round((birds * feedPer) / 100) / 10; // 1 decimal
    const feedType = phase.toLowerCase();
    try {
      await recordsAPI.createRecord(currentFarm.id, {
        type: 'feed_consumption',
        date: new Date().toISOString(),
        cohortId: c.id,
        quantity: qtyKg,
        unit: 'kg',
        category: 'feed',
        description: `Auto-plan: ${phase} feed`,
        metadata: { feedType }
      });
      alert(`Logged feed consumption: ${qtyKg} kg`);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to log feed');
    }
  };

  // Note: water logging intentionally omitted per product decision

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-500 text-white p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <div className="text-xs opacity-90">FARM</div>
            <h1 className="text-xl sm:text-3xl font-bold">{currentFarm?.name || 'Farm'}</h1>
            <div className="mt-0.5 sm:mt-1 text-xs sm:text-sm opacity-90">{currentFarm?.location || 'Location not set'}</div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {['owner','manager','editor'].includes(currentFarm?.currentUserRole) ? (
              <a href="/activities" className="inline-flex items-center rounded-lg sm:rounded-md bg-white text-primary-700 font-semibold px-3 sm:px-4 py-1.5 sm:py-2 shadow hover:shadow-md hover:bg-primary-50 text-xs sm:text-sm">
                Activities
              </a>
            ) : (
              <span className="inline-flex items-center rounded-lg sm:rounded-md bg-white text-gray-400 font-semibold px-3 sm:px-4 py-1.5 sm:py-2 shadow cursor-not-allowed text-xs sm:text-sm">
                Activities
              </span>
            )}
            {['owner','manager','editor'].includes(currentFarm?.currentUserRole) ? (
              <a href="/records" className="inline-flex items-center rounded-lg sm:rounded-md bg-white text-primary-700 font-semibold px-3 sm:px-4 py-1.5 sm:py-2 shadow hover:shadow-md hover:bg-primary-50 text-xs sm:text-sm">
                Records
              </a>
            ) : (
              <span className="inline-flex items-center rounded-lg sm:rounded-md bg-white text-gray-400 font-semibold px-3 sm:px-4 py-1.5 sm:py-2 shadow cursor-not-allowed text-xs sm:text-sm">
                Records
              </span>
            )}
            <a href={`/farm/${currentFarm?.id}/settings`} className="inline-flex items-center rounded-lg sm:rounded-md bg-white text-primary-700 font-semibold px-3 sm:px-4 py-1.5 sm:py-2 shadow hover:shadow-md hover:bg-primary-50 text-xs sm:text-sm">
              Settings
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6" id="flocks">
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Overview</h2>
          <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
            <label className="text-xs text-gray-600">Flock</label>
            <select className="input text-sm" value={selectedCohortId} onChange={(e)=>setSelectedCohortId(e.target.value)}>
              <option value="">All flocks</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}{c.name?.toLowerCase().includes('primary') ? ' (Primary)' : ''}</option>)}
            </select>
          </div>
          {loading ? (
            <p className="text-gray-500 text-sm">Loading farm details...</p>
          ) : currentFarm ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
              <Stat label="Location" value={currentFarm.location || 'Not specified'} />
              {selectedCohortId ? (
                <>
                  {(() => { const c = cohorts.find(x => x.id === selectedCohortId); return <Stat label="Type" value={c?.type || '—'} />; })()}
                  {(() => { const c = cohorts.find(x => x.id === selectedCohortId); if (!c) return null; return <Stat label="Age" value={`${computeAgeDays(c)} days`} />; })()}
                  <Stat label="Net Birds" value={cohortBirds?.netBirds ?? '—'} />
                  {(() => { const c = cohorts.find(x => x.id === selectedCohortId); return <Stat label="Breed" value={c?.breed || '—'} />; })()}
                </>
              ) : <></>}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No farm found.</p>
          )}
        </div>
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm md:col-span-2">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Quick Tips</h2>
          <ul className="text-xs sm:text-sm text-gray-700 list-disc pl-4 sm:pl-5 space-y-1.5 sm:space-y-2">
            <li>Log daily eggs and feed to see trends.</li>
            <li>Use AI Assistant for vaccination schedules and issues.</li>
            <li>Keep stock updated to avoid shortages.</li>
          </ul>
          
          {currentFarm && (
            <div className="mt-4 sm:mt-6">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2">Add Flock</h3>
              <form className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3" onSubmit={async (e)=>{
                e.preventDefault();
                if (!['owner','manager','editor'].includes(currentFarm?.currentUserRole)) { alert('You do not have permission to add flocks.'); return; }
                const payload = {
                  name: newFlock.name,
                  type: newFlock.type,
                  subtype: newFlock.subtype || undefined,
                  breed: newFlock.breed || undefined,
                  startQty: Number(newFlock.startQty || 0),
                  startDate: dateInputToApi(newFlock.startDate),
                  initialAgeDays: parseAgeDays(newFlock.chickAge),
                  housingType: newFlock.housingType || undefined,
                  feedingSystem: newFlock.feedingSystem || undefined,
                  managementSystem: newFlock.managementSystem || undefined,
                  targetMarketAgeDays: newFlock.targetMarketAgeDays === '' ? undefined : Number(newFlock.targetMarketAgeDays),
                };
                await farmAPI.createCohort(currentFarm.id, payload);
                setNewFlock({ name: '', type: 'broiler', subtype: '', breed: '', startQty: '', startDate: todayInputValue(), chickAge: '0', targetMarketAgeDays: '', housingType: '', feedingSystem: '', managementSystem: '' });
                alert('Flock added');
              }}>
                <input required className="input" placeholder="Flock name" value={newFlock.name} onChange={(e)=>setNewFlock(p=>({...p,name:e.target.value}))} />
                <select className="input" value={newFlock.type} onChange={(e)=>setNewFlock(p=>({...p,type:e.target.value, breed:'', subtype:''}))}>
                  <option value="broiler">Broiler</option>
                  <option value="layer">Layer</option>
                  <option value="dual">Dual-purpose</option>
                </select>
                {newFlock.type === 'dual' && (
                  <select className="input" value={newFlock.subtype} onChange={(e)=>setNewFlock(p=>({...p,subtype:e.target.value, breed:''}))}>
                    <option value="">Select Kienyeji subtype</option>
                    <option value="kienyeji_improved">Improved Kienyeji</option>
                    <option value="kienyeji_pure">Pure Kienyeji</option>
                  </select>
                )}
                <select className="input" value={newFlock.breed} onChange={(e)=>setNewFlock(p=>({...p,breed:e.target.value}))}>
                  <option value="">Select breed (optional)</option>
                  {(() => {
                    if (newFlock.type === 'broiler') return breedOptions.broiler;
                    if (newFlock.type === 'layer') return breedOptions.layer;
                    if (newFlock.type === 'dual') {
                      if (newFlock.subtype === 'kienyeji_improved') return breedOptions.dualImproved;
                      if (newFlock.subtype === 'kienyeji_pure') return breedOptions.dualPure;
                      return [...breedOptions.dualImproved, ...breedOptions.dualPure];
                    }
                    return [];
                  })().map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <input type="number" min="1" className="input" placeholder="Start qty" value={newFlock.startQty} onChange={(e)=>setNewFlock(p=>({...p,startQty:e.target.value}))} />
                <div className="sm:col-span-2 md:col-span-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                  <FlockTimingFields
                    required
                    idPrefix="add-flock-"
                    type={newFlock.type}
                    subtype={newFlock.subtype}
                    startDate={newFlock.startDate}
                    initialAgeDays={newFlock.chickAge}
                    targetMarketAgeDays={newFlock.targetMarketAgeDays}
                    onChange={(patch) => setNewFlock((p) => ({
                      ...p,
                      ...(patch.startDate !== undefined ? { startDate: patch.startDate } : {}),
                      ...(patch.initialAgeDays !== undefined ? { chickAge: patch.initialAgeDays } : {}),
                      ...(patch.targetMarketAgeDays !== undefined ? { targetMarketAgeDays: patch.targetMarketAgeDays } : {})
                    }))}
                  />
                </div>
                <select className="input" value={newFlock.housingType} onChange={(e)=>setNewFlock(p=>({...p,housingType:e.target.value}))}>
                  <option value="">Select housing (optional)</option>
                  {housingOptions.map(h => <option key={h} value={h}>{h.replace('_',' ')}</option>)}
                </select>
                <select className="input" value={newFlock.managementSystem} onChange={(e)=>setNewFlock(p=>({...p,managementSystem:e.target.value}))}>
                  <option value="">Select management system (optional)</option>
                  <option value="intensive">Intensive</option>
                  <option value="semi_intensive">Semi‑intensive</option>
                  <option value="free_range">Free‑range</option>
                </select>
                <select className="input" value={newFlock.feedingSystem} onChange={(e)=>setNewFlock(p=>({...p,feedingSystem:e.target.value}))}>
                  <option value="">Select feeding (optional)</option>
                  {feedingOptions.map(f => <option key={f} value={f}>{f.replace('_',' ')}</option>)}
                </select>
                <div className="md:col-span-3 pt-1">
                  <button type="submit" className={`btn-primary ${['owner','manager','editor'].includes(currentFarm?.currentUserRole) ? '' : 'opacity-50 cursor-not-allowed'}`} disabled={!['owner','manager','editor'].includes(currentFarm?.currentUserRole)}>
                    Add Flock
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 shadow-sm md:col-span-3">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">Today’s Feed & Water Plan</h3>
          {selectedCohortId ? (
            (() => {
              const c = cohorts.find(x => x.id === selectedCohortId);
              const ageDays = computeAgeDays(c);
              const birds = (cohortBirds?.netBirds != null ? Number(cohortBirds.netBirds) : Number(c?.startQty || 0));
              const rec = getFeedPhaseAndTarget(c?.type, c?.subtype, ageDays);
              const feedPer = rec.feedGPerBird;
              const totalFeedKg = Math.round((birds * feedPer) / 100) / 10; // one decimal
              const waterPer = getWaterTargetMl(c?.type, feedPer, ambientTempC);
              const totalWaterL = Math.round((birds * waterPer) / 100) / 10; // one decimal
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div className="rounded-lg border border-gray-200 p-3 sm:p-4">
                    <div className="text-gray-500 text-xs">Flock</div>
                    <div className="text-gray-900 font-medium text-sm sm:text-base">{c?.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{c?.type}{c?.subtype ? ` • ${c.subtype}` : ''}</div>
                    <div className="text-xs text-gray-500 mt-0.5">Age: {ageDays} days • Phase: {rec.phase}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3 sm:p-4">
                    <div className="text-gray-500 text-xs">Feed Target</div>
                    <div className="text-gray-900 font-medium text-sm sm:text-base">{feedPer} g/bird/day</div>
                    <div className="text-xs text-gray-500 mt-0.5 sm:mt-1">Total: {totalFeedKg} kg for {birds} birds</div>
                    <div className="mt-2 sm:mt-3">
                      <button className="btn-primary text-xs sm:text-sm px-3 py-1.5" onClick={()=>logFeed(c, birds, rec.phase, feedPer)}>Log feed</button>
                    </div>
                    <div className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-gray-600 hidden sm:block">
                      <div className="font-semibold text-gray-800 mb-1">Nutrient tips</div>
                      <ul className="list-disc list-inside space-y-0.5">
                        {getIngredientTips(c?.type, c?.subtype, rec.phase).slice(0, 2).map((t,i)=>(
                          <li key={i} className="line-clamp-2">{t}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3 sm:p-4 sm:col-span-2 md:col-span-1">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-gray-500 text-xs">Water Target</div>
                        <div className="text-gray-900 font-medium text-sm sm:text-base">{waterPer} ml/bird/day</div>
                        <div className="text-xs text-gray-500 mt-0.5 sm:mt-1">Total: {totalWaterL} L for {birds} birds</div>
                      </div>
                      <div className="text-right">
                        <label className="text-[10px] sm:text-xs text-gray-500">Ambient °C</label>
                        <input className="input mt-1 w-16 sm:w-20 text-sm" value={ambientTempC} onChange={(e)=>setAmbientTempC(e.target.value)} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="text-xs sm:text-sm text-gray-600">Select a flock in Overview to see today’s feed and water plan.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wide">{label}</div>
    <div className="text-gray-900 font-medium text-xs sm:text-sm">{value}</div>
  </div>
);

export default FarmManagement;
